import {
  IssueCaseCategory,
  IssueCaseCreatorType,
  IssueCaseDoc,
  IssueCaseSubcategory,
  IssueProduct,
  IssueSourceType,
} from '@gooduncles/gu-app-schema';
import { message, notification } from 'antd';
import { useCallback } from 'react';
import { useAuth } from 'src/stores/auth-context';

import { AuditLogger } from 'src/utils/audit-logger';
import { createIssueCase, updateIssueCase } from 'src/utils/firebase/firebase-short-cut';
import { getCategoryLabel } from 'src/utils/issue/issue-data-util';
import { calculateIssueAmount, getIssueCaseInitialData, getIssueCaseSubcategories } from 'src/utils/issue/issue-util';

const auditLogger = AuditLogger.getInstance();

interface IssueCaseSourceParams {
  sourceType: IssueSourceType;
  sourceId: string;
  sourceReferenceDate: string;
  storeId: string;
  storeName: string;
}

interface UseIssueCaseActionsOptions {
  getSourceParams: () => IssueCaseSourceParams;
  canCreate?: () => { allowed: boolean; message?: string };
}

const useIssueCaseActions = (options: UseIssueCaseActionsOptions) => {
  const { user } = useAuth();
  const { getSourceParams, canCreate } = options;

  const createNewIssueCase = useCallback(
    async (category: string) => {
      if (!user) {
        message.error('사용자 정보가 없습니다. 다시 로그인 해주세요.');
        return;
      }

      if (canCreate) {
        const { allowed, message: errorMessage } = canCreate();
        if (!allowed) {
          message.error(errorMessage ?? '이슈 케이스를 생성할 수 없습니다.');
          return;
        }
      }

      const typedCategory = category as IssueCaseCategory;
      const subcategoryMap = getIssueCaseSubcategories(typedCategory);
      const subcategory = subcategoryMap ? Object.keys(subcategoryMap)[0] : null;
      const sourceParams = getSourceParams();

      const base = getIssueCaseInitialData({
        ...sourceParams,
        creatorId: user._id,
        creatorType: user.role as IssueCaseCreatorType,
        category: typedCategory,
        subcategory: subcategory as IssueCaseSubcategory | null,
      });

      try {
        const issueCaseId = await createIssueCase(base, { audit: { suppress: true } });
        auditLogger.log({
          action: 'CREATE',
          entity: 'issue',
          entityId: issueCaseId,
          summary: `이슈 케이스 생성 - ${getCategoryLabel(typedCategory)}`,
          metadata: { category: typedCategory, storeId: sourceParams.storeId, sourceType: sourceParams.sourceType },
        });
        message.success(`${getCategoryLabel(typedCategory)} 이슈 케이스가 생성되었습니다.`);
      } catch (error) {
        console.error(error);
        notification.error({ message: '이슈 케이스 생성에 실패했습니다. 다시 시도해주세요.' });
      }
    },
    [user, canCreate, getSourceParams],
  );

  const updateIssueCaseProducts = useCallback(async (issueCaseDoc: IssueCaseDoc, products: IssueProduct[]) => {
    try {
      const amount = calculateIssueAmount(products);
      await updateIssueCase(issueCaseDoc._id, { products, amount }, { audit: { suppress: true } });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'issue',
        entityId: issueCaseDoc._id,
        summary: `이슈 케이스 상품 업데이트 (${products.length}개)`,
        changes: [
          { field: 'products', before: issueCaseDoc.products?.length ?? 0, after: products.length },
          { field: 'amount', before: issueCaseDoc.amount, after: amount },
        ],
      });
    } catch (error) {
      console.error(error);
      notification.error({ message: '이슈 케이스 상품 업데이트에 실패했습니다. 다시 시도해주세요.' });
    }
  }, []);

  return { createNewIssueCase, updateIssueCaseProducts };
};

export default useIssueCaseActions;
